import { useGetTagsByResourceQuery, useSetTagsForResourceMutation } from '../../../../apps/admin/components/TagsInput/TagsInput.graphql.generated';
import { useScheduleEditorGetItemByIdQuery, useScheduleEditorUpdateItemMutation } from '../ScheduleEditor.graphql.generated';
import { useCallback, useMemo } from 'react';
import type { ScheduleEditorData } from '../ScheduleEditor.types';
import { ScheduleFormDisplay, ScheduleFormDisplaySkeleton } from './form/ScheduleFormDisplay';
import React from 'react';
import type { ScheduleEditorProps } from '../ScheduleEditor';
import { EMPTY_FORM_DATA } from '../ScheduleEditor.constants';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useDeleteScheduleItem } from '../hooks/useDeleteScheduleItem';
import { formatDateAsISO8601TZ, useGetEventTimezoneAndDates } from './form/hooks/useGetEventTimezone';
import { addError } from '@shared/utils/logger';

const RESOURCE_TYPE = 'SCHEDULE_ITEM';

export const EditScheduleItem = (props: ScheduleEditorProps & { mode: 'edit' }) => {
  const { scheduleItemId, eventId, onSuccess, onError, onCancel, refetch } = props;
  const { deleteScheduleItem } = useDeleteScheduleItem({
    refetch,
  });
  const { data: scheduleQueryResults, loading } = useScheduleEditorGetItemByIdQuery({
    variables: {
      scheduleId: scheduleItemId,
    },
    batchMode: 'fast',
  });

  // Fetch initial tags for the resource
  const { data: tagsData, loading: tagsLoading } = useGetTagsByResourceQuery({
    variables: { eventId, resourceId: scheduleItemId },
    batchMode: 'fast',
  });

  const { timezone: eventTimezone, loading: timezoneLoading } = useGetEventTimezoneAndDates();

  const [updateItem, { loading: isSaving }] = useScheduleEditorUpdateItemMutation();
  const [setTagsForResource] = useSetTagsForResourceMutation();

  const handleSubmit = useCallback(
    async (data: ScheduleEditorData) => {
      try {
        // Update schedule item
        const results = await updateItem({
          variables: {
            payload: {
              scheduleId: scheduleItemId,
              startTimeISO8601TZ: formatDateAsISO8601TZ(data.startTime, data.timezone),
              endTimeISO8601TZ: formatDateAsISO8601TZ(data.endTime, data.timezone),
              description: data.description,
              name: data.name,
              virtualEventLink: data.virtualEventUrl,
              dressCode: data.dressCode,
              location: data.location,
              placeId: data.placeId,
              showMap: data.showMap,
            },
          },
          refetchQueries: ['ScheduleEditorGetItemById', ...(refetch ?? [])],
          awaitRefetchQueries: true,
        });

        // Set tags for the resource
        await setTagsForResource({
          variables: {
            eventId,
            tagIds: data.tags.map(t => t.id),
            resourceId: scheduleItemId,
            resourceType: RESOURCE_TYPE,
          },
          refetchQueries: ['GetTagsByResource'],
        });

        if (results.data?.updateEventScheduleItem) {
          onSuccess(results.data.updateEventScheduleItem);
        } else {
          onError?.(results.errors);
        }
      } catch (error) {
        addError(error, { context: 'EditScheduleItem', message: 'Failed to save schedule item' });
        onError?.(error);
      }
    },
    [updateItem, setTagsForResource, eventId, onError, onSuccess, refetch, scheduleItemId],
  );

  const initialData = useMemo(() => {
    const scheduleData = scheduleQueryResults?.getAdminEventScheduleItemById;

    if (!scheduleData) {
      return EMPTY_FORM_DATA;
    }

    // Get initial tags from the tags query
    const initialTags = tagsData?.getTagsByResource ?? [];

    const data: ScheduleEditorData = {
      name: scheduleData.name,
      description: scheduleData.description ?? '',
      startTime: scheduleData.startTime ? new Date(scheduleData.startTime?.dateString) : null,
      endTime: scheduleData.endTime ? new Date(scheduleData.endTime?.dateString) : null,
      // Use schedule item's timezone if set, otherwise fall back to event timezone
      timezone: scheduleData.timezone?.replace(' ', '_') || eventTimezone || '',
      virtualEventUrl: scheduleData.virtualEventLink?.urlLinkText ?? null,
      dressCode: scheduleData.dressCode ?? '',
      location: scheduleData.location ?? null,
      placeId: scheduleData.locationInfo?.placeId ?? null,
      showMap: scheduleData.showMap ?? false,
      tags: initialTags,
    };

    return data;
  }, [scheduleQueryResults, tagsData, eventTimezone]);

  return (
    <SkeletonGroup
      placeholder={<ScheduleFormDisplaySkeleton />}
      isReady={!loading && !tagsLoading && !timezoneLoading && !!scheduleQueryResults}
    >
      <ScheduleFormDisplay
        isSaving={isSaving}
        data={initialData}
        onSubmit={handleSubmit}
        onCancel={() => {
          onCancel?.();
        }}
        onDelete={() => {
          deleteScheduleItem(scheduleItemId);
          onCancel?.();
        }}
        eventId={eventId}
      />
    </SkeletonGroup>
  );
};
